/*
 * 犬｜購入オプション（一回のみ / 定期）
 *
 * inu-selling-plans スニペットが出す <inu-selling-plans> の中身を受け持つ。
 *
 * 1. ラジオボタンの選択を、同じフォームの hidden input（name="selling_plan"）へ写す。
 *    これを入れないと selling_plan が空で送信され、Shopify が定期の行を
 *    一回のみの行と同一とみなしてカートで1行にまとめてしまう。
 *
 * 2. バリアントが変わったら選択肢と価格を描き直す。
 *    Horizon はバリアントを切り替えてもバリアントピッカー自身しか差し替えないため、
 *    これが無いと「3食を選んだときの定期価格」が1食に表示されたままになる。
 *    プランの無いバリアント（1食）へ移ったときは選択肢ごと消し、
 *    hidden の selling_plan も空に戻す（前のプランが残ったまま買われるのを防ぐ）。
 */

import { StandardEvents } from '@shopify/events';

class InuSellingPlans extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    // 商品ページはセクション、モーダルは dialog が変更イベントの通り道になる
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('change', this.#handlePlanChange);

    // 定期便専用の商品では最初から定期が選ばれている。
    // hidden を空のままにすると一回のみ購入として送られるので、初期状態をここで写す
    const checked = this.querySelector('[data-inu-plan]:checked');
    if (checked instanceof HTMLInputElement && checked.value) this.#writeSellingPlan(checked.value);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener('change', this.#handlePlanChange);
  }

  /**
   * バリアントごとの価格表。スニペットが埋め込んだ JSON を読む
   * @returns {{ groupName: string, onetimeLabel: string, allowOnetime: boolean, note: string, variants: Record<string, { price: string, plans: Array<{ id: string, name: string, price: string, saved: number }> }> } | null}
   */
  get #data() {
    const script = this.querySelector('script[type="application/json"][data-inu-plan-data]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[inu-selling-plans] 価格表を読めませんでした', error);
      return null;
    }
  }

  /** 選択をフォームの hidden へ写す */
  #handlePlanChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-inu-plan')) return;
    this.#writeSellingPlan(target.value);
  };

  /** バリアントが変わったら描き直す */
  #handleProductSelect = (event) => {
    // detail の入れ子は @shopify/events の版によって変わるので、両方の形を見る
    const variantId = event.detail?.variantId ?? event.detail?.detail?.variantId;

    if (variantId) {
      this.setVariant(variantId);
      return;
    }

    // 同期の detail に無いときは、取得したバリアント情報から拾う
    event.promise
      ?.then(({ detail }) => {
        const resolvedId = detail?.resource?.id;
        if (resolvedId) this.setVariant(resolvedId);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[inu-selling-plans] バリアントを特定できませんでした', error);
      });
  };

  /**
   * 指定したバリアントの選択肢に描き直す。モーダルの select からも呼ぶ
   * @param {string | number} variantId
   */
  setVariant(variantId) {
    const key = String(variantId);
    if (this.dataset.variant === key) return;

    const data = this.#data;
    const entry = data?.variants?.[key];
    if (!data || !entry) return;

    this.dataset.variant = key;
    // 定期便専用では先頭のプランが選ばれた状態で描き直すので、hidden もそれに合わせる
    const firstPlanId = data.allowOnetime === false ? (entry.plans[0]?.id ?? '') : '';
    this.#writeSellingPlan(firstPlanId);

    const field = this.querySelector('[data-inu-plans-field]');

    // プランの無いバリアントでは選択肢ごと出さない
    if (entry.plans.length === 0) {
      field?.remove();
      return;
    }

    const next = this.#buildField(data, entry);
    if (field) {
      field.replaceWith(next);
    } else {
      this.appendChild(next);
    }
  }

  /**
   * 選択肢を組み立てる。マークアップは inu-selling-plans / inu-price と同じ形にそろえる
   * @param {{ groupName: string, onetimeLabel: string, allowOnetime: boolean, note: string }} data
   * @param {{ price: string, plans: Array<{ id: string, name: string, price: string, saved: number }> }} entry
   */
  #buildField(data, entry) {
    const uid = this.dataset.uid ?? '';
    const field = document.createElement('fieldset');
    field.className = 'inu-plans';
    field.setAttribute('data-inu-plans-field', '');

    const legend = document.createElement('legend');
    legend.className = 'inu-plans__legend';
    legend.textContent = data.groupName;
    field.appendChild(legend);

    // 定期便専用の商品では一回のみ購入を出さず、先頭のプランを選んだ状態にする
    const allowOnetime = data.allowOnetime !== false;
    if (allowOnetime) {
      field.appendChild(this.#buildOption(uid, '', data.onetimeLabel, entry.price, 0, data.note, true));
    }

    entry.plans.forEach((plan, index) => {
      const checked = !allowOnetime && index === 0;
      field.appendChild(this.#buildOption(uid, plan.id, plan.name, plan.price, plan.saved, data.note, checked));
    });

    return field;
  }

  /**
   * 選択肢1つ分
   * @param {string} uid
   * @param {string} value - 空文字なら一回のみ購入
   * @param {string} label
   * @param {string} price - 通貨記号を含まない価格（例「3,600」）
   * @param {number} saved - 割引率。0 なら札を出さない
   * @param {string} note - 税表記
   * @param {boolean} checked
   */
  #buildOption(uid, value, label, price, saved, note, checked) {
    const option = document.createElement('label');
    option.className = 'inu-plans__option';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.className = 'inu-plans__radio';
    radio.name = `inu-plan-${uid}`;
    radio.value = value;
    radio.setAttribute('data-inu-plan', '');
    radio.checked = checked;
    option.appendChild(radio);

    const text = document.createElement('span');
    text.className = 'inu-plans__text';

    const name = document.createElement('span');
    name.className = 'inu-plans__name';
    name.textContent = label;

    if (saved > 0) {
      const badge = document.createElement('span');
      badge.className = 'inu-plans__badge inu-num';
      badge.textContent = `${saved}%OFF`;
      name.appendChild(badge);
    }

    text.appendChild(name);
    text.appendChild(this.#buildPrice(price, note));
    option.appendChild(text);

    return option;
  }

  /**
   * inu-price スニペットと同じマークアップ
   * @param {string} price
   * @param {string} note
   */
  #buildPrice(price, note) {
    const wrap = document.createElement('span');
    wrap.className = 'inu-yen inu-plans__price';

    const value = document.createElement('span');
    value.className = 'inu-yen__value inu-num';
    value.textContent = price;

    const unit = document.createElement('span');
    unit.className = 'inu-yen__unit';
    unit.textContent = '円';

    const noteEl = document.createElement('span');
    noteEl.className = 'inu-yen__note';
    noteEl.textContent = note || '（税込）';

    wrap.append(value, unit, noteEl);
    return wrap;
  }

  /**
   * フォームの hidden input へ書き込む
   * @param {string} value
   */
  #writeSellingPlan(value) {
    const form = this.closest('form');
    const planInput = form?.querySelector('input[name="selling_plan"]');
    if (planInput instanceof HTMLInputElement) planInput.value = value;
  }
}

if (!customElements.get('inu-selling-plans')) {
  customElements.define('inu-selling-plans', InuSellingPlans);
}
